import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/lib/auth/auth-context';
import { useDebounced } from '@/lib/use-debounced';
import { cn } from '@/lib/utils';
import { foodsQueryOptions } from '@/screens/foods/foods-query';

export interface IngredientPickerValue {
  readonly id: string;
  readonly name: string;
}

export interface IngredientPickerProps {
  readonly value: IngredientPickerValue | null;
  readonly onChange: (next: IngredientPickerValue | null) => void;
  readonly disabled?: boolean | undefined;
}

/**
 * Debounced catalog search, scoped to active ingredients, for pointing a
 * recipe line at a row. Shared by the food record's recipe panel and the
 * new-food page, which both need exactly this search and this rendering.
 * `MergeDialog` inlines a similar pattern for picking a merge survivor; it
 * filters and renders differently and stays its own.
 */
export function IngredientPicker({ value, onChange, disabled = false }: IngredientPickerProps) {
  const { api } = useAuth();
  const [term, setTerm] = useState('');
  const debounced = useDebounced(term, 260);

  const candidates = useQuery({
    ...foodsQueryOptions(api, { search: debounced, kind: 'ingredient', activity: 'active' }),
    enabled: debounced.length >= 2,
  });

  if (value !== null) {
    return (
      <div className="flex h-9 items-center justify-between gap-2 rounded-md border border-input bg-card px-2.5 text-sm">
        <span className="min-w-0 flex-1 truncate">{value.name}</span>
        <Button
          variant="ghost"
          size="sm"
          disabled={disabled}
          onClick={() => {
            onChange(null);
          }}
        >
          Change
        </Button>
      </div>
    );
  }

  return (
    <div className="relative">
      <Input
        type="search"
        value={term}
        disabled={disabled}
        placeholder="Search ingredients…"
        onChange={(event) => {
          setTerm(event.target.value);
        }}
      />
      {debounced.length < 2 ? null : (
        <ul className="absolute z-10 mt-1 max-h-[180px] w-full overflow-y-auto rounded-md border border-border bg-popover shadow-md">
          {candidates.isPending ? (
            <li className="px-3 py-2 text-[13px] text-muted-foreground">Searching…</li>
          ) : candidates.data === undefined || candidates.data.items.length === 0 ? (
            <li className="px-3 py-2 text-[13px] text-muted-foreground">No active ingredient matches.</li>
          ) : (
            candidates.data.items.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => {
                    setTerm('');
                    onChange({ id: item.id, name: item.name });
                  }}
                  className={cn(
                    'w-full truncate px-3 py-2 text-left text-sm text-foreground transition-colors hover:bg-accent',
                  )}
                >
                  {item.name}
                </button>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
